/**
 * DeleteDialog — Confirmation dialog before deleting a conversation.
 *
 * Layer: C (UX/CLI)
 * Spec: Uppdrag C §2.2, §6
 *
 * @decision Uses native HTML <dialog> element — no shadcn/ui Dialog dependency needed.
 * @decision Accessible: focus trap via dialog, ESC to close, aria labels.
 * @decision Styled to match dark theme (zinc-900 background).
 */

import { useRef, useEffect, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────

export interface DeleteDialogProps {
  isOpen: boolean;
  conversationTitle: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// ─── Component ────────────────────────────────────────────────────────

export function DeleteDialog({
  isOpen,
  conversationTitle,
  onConfirm,
  onCancel,
}: DeleteDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync open state with dialog element
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Handle backdrop click (close on click outside)
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        onCancel();
      }
    },
    [onCancel],
  );

  // Handle ESC key (native dialog behavior, but we sync state)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onCancel();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onCancel]);

  const title = conversationTitle || 'Ny konversation';

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      className={[
        // Backdrop
        'backdrop:bg-black/60 backdrop:backdrop-blur-sm',
        // Dialog box
        'bg-bg-secondary border border-border/60 rounded-xl shadow-2xl',
        'p-0 max-w-sm w-[calc(100%-2rem)]',
        // Animation
        'open:animate-in open:fade-in-0 open:zoom-in-95',
      ].join(' ')}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-desc"
    >
      <div className="p-6">
        {/* Warning icon */}
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-500/10 mb-4">
          <svg
            className="text-red-400"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </div>

        <h3
          id="delete-dialog-title"
          className="text-base font-semibold text-text-primary mb-1"
        >
          Ta bort konversation?
        </h3>

        <p
          id="delete-dialog-desc"
          className="text-sm text-text-secondary mb-6"
        >
          <span className="font-medium text-text-secondary">&ldquo;{title}&rdquo;</span>{' '}
          kommer att raderas permanent. Detta kan inte ångras.
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className={[
              'px-4 py-2 rounded-lg text-sm font-medium',
              'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary',
              'border border-border/60',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-border',
              'transition-colors duration-150',
            ].join(' ')}
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={[
              'px-4 py-2 rounded-lg text-sm font-medium',
              'bg-red-600 text-white hover:bg-red-500',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
              'transition-colors duration-150',
            ].join(' ')}
          >
            Radera
          </button>
        </div>
      </div>
    </dialog>
  );
}
